import { z } from "zod";

// Organizations (tenants), their members and who is signed in: the vocabulary and request and response shapes the API
// and the frontend share.

/** The business sections of the app. A member's access is set per section. */
export const SECTION_IDS = ["finance", "legal", "people", "equity"] as const;
export type SectionId = (typeof SECTION_IDS)[number];

export const SECTION_LABELS: Readonly<Record<SectionId, string>> = {
	finance: "Finance",
	legal: "Legal & Contracts",
	people: "People & HR",
	equity: "Equity & Investors",
};

/**
 * What a member's access is set per: the four business sections, and the Directory, which is not a section of its own
 * — the rail links to it above the sections, and it has no section colour — but which a member is given access to the
 * same way. A work item belongs to one area, which is who may see it (`./work.ts`).
 */
export const ACCESS_AREAS = [...SECTION_IDS, "directory"] as const;
export type AccessArea = (typeof ACCESS_AREAS)[number];

export const ACCESS_AREA_LABELS: Readonly<Record<AccessArea, string>> = {
	...SECTION_LABELS,
	directory: "Directory",
};

/** Owners and admins manage the organization, but only owners make admins and owners; members get access per section. */
export const MEMBER_ROLES = ["owner", "admin", "member"] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

export const MEMBER_ROLE_LABELS: Readonly<Record<MemberRole, string>> = {
	owner: "Owner",
	admin: "Admin",
	member: "Member",
};

/**
 * The roles someone with `role` may give a member whose role is `currentRole`, or a new member when it is undefined.
 * Owners give every role. Admins make nobody an admin or an owner and leave owners alone, but they may keep an admin an
 * admin, so they can still rename one or make them a member. Members change nobody.
 */
export function assignableRoles(role: MemberRole, currentRole?: MemberRole): readonly MemberRole[] {
	if (role === "owner") {
		return MEMBER_ROLES;
	}
	if (role === "member" || currentRole === "owner") {
		return [];
	}
	return currentRole === "admin" ? ["admin", "member"] : ["member"];
}

/** What a member can do in one area. */
export const ACCESS_LEVELS = ["none", "reader", "editor"] as const;
export type AccessLevel = (typeof ACCESS_LEVELS)[number];

export const ACCESS_LEVEL_LABELS: Readonly<Record<AccessLevel, string>> = {
	none: "No access",
	reader: "Reader",
	editor: "Editor",
};

/** Owners and admins are editors in every area. */
export const EDITOR_EVERYWHERE = {
	finance: "editor",
	legal: "editor",
	people: "editor",
	equity: "editor",
	directory: "editor",
} as const satisfies Record<AccessArea, AccessLevel>;

/** No access to any area: what a member has until somebody gives them some. */
export const NO_ACCESS_ANYWHERE = {
	finance: "none",
	legal: "none",
	people: "none",
	equity: "none",
	directory: "none",
} as const satisfies Record<AccessArea, AccessLevel>;

export const AUDIT_CATEGORIES = [
	"session",
	"company",
	"team",
	"people",
	"directory",
	"security",
	"documents",
	"finance",
	"integrations",
	"agents",
] as const;
export type AuditCategory = (typeof AUDIT_CATEGORIES)[number];

/**
 * What the Audit log page lists entries by: seven sections, each one category or a few that belong together. Access is
 * who may sign in and what they may do — sign-ins, passwords and two-step verification, the team — and Organization the
 * company's own settings and the services it connects. An entry keeps the category it was recorded in; its section
 * follows from it (`auditSectionOf`), so no entry is moved when the sections are drawn otherwise.
 */
export const AUDIT_SECTIONS = [
	"access",
	"organization",
	"people",
	"directory",
	"documents",
	"finance",
	"agents",
] as const;
export type AuditSection = (typeof AUDIT_SECTIONS)[number];

/** The categories each section lists, every category in exactly one. */
export const AUDIT_SECTION_CATEGORIES: Readonly<Record<AuditSection, readonly AuditCategory[]>> = {
	access: ["session", "security", "team"],
	organization: ["company", "integrations"],
	people: ["people"],
	directory: ["directory"],
	documents: ["documents"],
	finance: ["finance"],
	agents: ["agents"],
};

/** The section an entry of `category` is listed under. */
export function auditSectionOf(category: AuditCategory): AuditSection {
	const section = AUDIT_SECTIONS.find(candidate => AUDIT_SECTION_CATEGORIES[candidate].includes(category));
	if (section === undefined) {
		throw new Error(`The category "${category}" is in no section`);
	}
	return section;
}

/** Reporting currencies the company identity offers, as ISO 4217 codes. */
export const CURRENCIES = [
	"USD",
	"EUR",
	"GBP",
	"CHF",
	"CAD",
	"AUD",
	"JPY",
	"SEK",
	"NOK",
	"DKK",
	"PLN",
	"HUF",
	"CZK",
] as const;

/** High-level industry groups for the company identity. */
export const INDUSTRY_IDS = [
	"technology",
	"manufacturing",
	"retail",
	"construction",
	"professional-services",
	"healthcare",
	"financial-services",
	"hospitality",
	"real-estate",
	"education",
	"transportation",
	"agriculture",
	"energy",
	"media",
	"marketing",
	"nonprofit",
	"other",
] as const;
export type IndustryId = (typeof INDUSTRY_IDS)[number];

/** Form labels of the industries; the audit log uses them to say what an identity was changed to. */
export const INDUSTRY_LABELS: Readonly<Record<IndustryId, string>> = {
	technology: "Technology / Software",
	manufacturing: "Manufacturing / Industrial",
	retail: "Retail / E-commerce",
	construction: "Construction / Trades",
	"professional-services": "Professional services",
	healthcare: "Healthcare / Medical",
	"financial-services": "Financial services / Fintech",
	hospitality: "Hospitality / Food & beverage",
	"real-estate": "Real estate",
	education: "Education / Training",
	transportation: "Transportation / Logistics",
	agriculture: "Agriculture / Food production",
	energy: "Energy / Utilities",
	media: "Media / Entertainment",
	marketing: "Marketing / Advertising",
	nonprofit: "Nonprofit / Public sector",
	other: "Other",
};

/** Every identity field, in form order. */
export const IDENTITY_FIELDS = [
	"companyName",
	"legalName",
	"streetAddress",
	"city",
	"state",
	"zipCode",
	"country",
	"timeZone",
	"currency",
	"industry",
] as const;
export type IdentityField = (typeof IDENTITY_FIELDS)[number];

/** Form labels of the identity fields; the audit log uses them to say what changed. */
export const IDENTITY_FIELD_LABELS: Readonly<Record<IdentityField, string>> = {
	companyName: "Company name",
	legalName: "Legal entity name",
	streetAddress: "Street address",
	city: "City",
	state: "State",
	zipCode: "ZIP code",
	country: "Country",
	timeZone: "Time zone",
	currency: "Primary currency",
	industry: "Industry",
};

/** Whether dates can be formatted in `timeZone`, an IANA name such as `"Europe/Ljubljana"` or `"UTC"`. */
export function isTimeZone(timeZone: string): boolean {
	try {
		new Intl.DateTimeFormat("en-US", { timeZone });
		return true;
	} catch {
		return false;
	}
}

/** An email address as the API keeps it: without surrounding spaces and in lowercase. */
export const emailSchema = z.string().trim().toLowerCase().pipe(z.email("Enter a valid email address"));

/** A password someone chooses. The Cognito user pool's policy asks for the same 12 characters. */
export const newPasswordSchema = z
	.string()
	.min(12, "Use at least 12 characters")
	.max(128, "Use at most 128 characters");

function optionalText(maxLength: number) {
	return z.string().trim().max(maxLength, `Use at most ${maxLength} characters`);
}

/** Who the company is on invoices, contracts and reports. Only the company name is needed to create an organization. */
export const companyIdentitySchema = z.object({
	companyName: z.string().trim().min(1, "Company name is required").max(120, "Use at most 120 characters"),
	legalName: optionalText(160),
	streetAddress: optionalText(200),
	city: optionalText(100),
	state: optionalText(100),
	zipCode: optionalText(20),
	country: optionalText(100),
	timeZone: z.string().refine(isTimeZone, "Pick a time zone"),
	currency: z.enum(CURRENCIES),
	/** Empty until someone picks one. */
	industry: z.enum(INDUSTRY_IDS).or(z.literal("")),
});
export type CompanyIdentity = z.infer<typeof companyIdentitySchema>;

/**
 * An organization's security rules. `requireMfa` makes every member sign in with an authenticator app: the API refuses
 * to open a session for a member without one and walks them through setting one up instead. It defaults to false so
 * that an organization saved before the setting existed reads as not requiring it.
 */
export const organizationSecuritySchema = z.object({ requireMfa: z.boolean().default(false) });
export type OrganizationSecurity = z.infer<typeof organizationSecuritySchema>;

/** What Settings › Security shows: the rule, and how many members are ready for it. */
export const organizationSecurityStatusSchema = z.object({
	requireMfa: z.boolean(),
	/** Members who have an authenticator app. */
	membersWithMfa: z.int().nonnegative(),
	memberCount: z.int().nonnegative(),
});
export type OrganizationSecurityStatus = z.infer<typeof organizationSecurityStatusSchema>;

/** The one value a change to the security rules records in the audit log. */
export const MFA_REQUIREMENT_LABEL = "Two-step verification";
export const MFA_REQUIREMENT_VALUES = { required: "Required for everyone", optional: "Optional" } as const;
export function mfaRequirementText(requireMfa: boolean): string {
	return requireMfa ? MFA_REQUIREMENT_VALUES.required : MFA_REQUIREMENT_VALUES.optional;
}

export type MemberAccess = Readonly<Record<AccessArea, AccessLevel>>;

/**
 * A member's access per area. It is read back as a partial record and filled in, so a member stored before an area
 * existed — every member stored before the Directory did — reads as having no access to it rather than failing to
 * parse. A request that leaves an area out means the same thing, which fails closed.
 */
export const memberAccessSchema = z
	.partialRecord(z.enum(ACCESS_AREAS), z.enum(ACCESS_LEVELS))
	.transform((access): MemberAccess => {
		const filled: Record<AccessArea, AccessLevel> = { ...NO_ACCESS_ANYWHERE };
		for (const area of ACCESS_AREAS) {
			filled[area] = access[area] ?? "none";
		}
		return filled;
	});

export const teamMemberSchema = z.object({
	/** The member's Cognito user id (the `sub` claim of their tokens). */
	id: z.string().min(1),
	name: z.string(),
	email: z.email(),
	role: z.enum(MEMBER_ROLES),
	/** Access per area. Owners and admins are editors everywhere. */
	access: memberAccessSchema,
});
export type TeamMember = z.infer<typeof teamMemberSchema>;

/** What the team page sends to add a member. Owners and admins are saved as editors everywhere, whatever `access` says. */
export const addTeamMemberRequestSchema = z.object({
	name: z.string().trim().min(1, "Name is required").max(80, "Use at most 80 characters"),
	email: emailSchema,
	role: z.enum(MEMBER_ROLES),
	access: teamMemberSchema.shape.access,
});
export type AddTeamMemberRequest = z.infer<typeof addTeamMemberRequestSchema>;

/** What the team page sends to change a member. The email cannot change: remove the member and add them again. */
export const updateTeamMemberRequestSchema = addTeamMemberRequestSchema.omit({ email: true });
export type UpdateTeamMemberRequest = z.infer<typeof updateTeamMemberRequestSchema>;

/**
 * At most this many values on one entry: a company identity has ten fields, a member eight, a person twelve and a
 * contract eleven.
 */
const CHANGES_PER_ENTRY = 20;

/** One value an audit entry records, in the words the form uses. */
export const auditChangeSchema = z.object({
	/** What the value is called where it is changed: "Company name", "Role", "People & HR". */
	label: z.string().min(1).max(80),
	/** The value after the change, or an empty string when it was cleared. */
	value: z.string().max(256),
	/** The value before the change; absent when there was none, as for a member who was just added. */
	previous: z.string().max(256).optional(),
});
export type AuditChange = z.infer<typeof auditChangeSchema>;

/**
 * What an automated run took, on an entry that records one: an agent's run on a document, Bedrock Data Automation's
 * read of one, a read of the ledger from the bank. An agent's run carries its turns and tokens, Bedrock's read its pages.
 */
export const auditRunSchema = z.object({
	/** When it started; absent where nothing says, as for Bedrock's read. */
	startedAt: z.iso.datetime().optional(),
	finishedAt: z.iso.datetime(),
	/** Whether it finished what it was asked to do. A failed run is recorded as surely as a finished one. */
	outcome: z.enum(["finished", "failed"]),
	/** How many times the model was asked. */
	turns: z.number().int().nonnegative().optional(),
	/** The tokens the model was sent, every turn together. */
	inputTokens: z.number().int().nonnegative().optional(),
	/** The tokens the model wrote back, every turn together. */
	outputTokens: z.number().int().nonnegative().optional(),
	/** The pages Bedrock read. */
	pages: z.number().int().nonnegative().optional(),
});
export type AuditRun = z.infer<typeof auditRunSchema>;

/**
 * What an audit entry can be about, each a page of its own in the app: an Intake document, a person or another party of
 * the Directory (`PARTY_KINDS`), a member of the team, a transaction of the ledger, an API key.
 */
export const AUDIT_SUBJECT_KINDS = [
	"document",
	"person",
	"vendor",
	"customer",
	"other",
	"member",
	"transaction",
	"api-key",
] as const;
export type AuditSubjectKind = (typeof AUDIT_SUBJECT_KINDS)[number];

/** The record an entry is about, by its kind and the id the API names it by in its own paths. */
export const auditSubjectSchema = z.object({
	kind: z.enum(AUDIT_SUBJECT_KINDS),
	id: z.string().min(1).max(128),
});
export type AuditSubject = z.infer<typeof auditSubjectSchema>;

/** A subject as `GET /api/audit-log` takes it in its query: "document:<file id>". */
export function auditSubjectParameter({ kind, id }: AuditSubject): string {
	return `${kind}:${id}`;
}

/** The subject a query names ("document:<file id>"), or undefined for one that names none. */
export function parseAuditSubjectParameter(parameter: string): AuditSubject | undefined {
	const at = parameter.indexOf(":");
	const parsed = auditSubjectSchema.safeParse({ kind: parameter.slice(0, at), id: parameter.slice(at + 1) });
	return at > 0 && parsed.success ? parsed.data : undefined;
}

export const auditEntrySchema = z.object({
	id: z.string().min(1),
	/** When it happened, as an ISO 8601 timestamp in UTC. */
	at: z.iso.datetime(),
	/** Who did it: a person's email, an automation's name ("Directory agent"), or an API key (`apiKeyActor`). */
	actor: z.string(),
	category: z.enum(AUDIT_CATEGORIES),
	action: z.string(),
	/** What changed, or an empty string. */
	detail: z.string(),
	/**
	 * What the change set, newest value first, for an entry that records values; absent on every other entry and on
	 * entries written before the audit log recorded values.
	 */
	changes: z.array(auditChangeSchema).max(CHANGES_PER_ENTRY).optional(),
	/** What an automated run took, for an entry that records one (`agent-runs.ts`); absent on every other entry. */
	run: auditRunSchema.optional(),
	/**
	 * What the entry is about, for an entry about a record with a page of its own; absent on every other entry, and on
	 * entries recorded before the audit log named one.
	 */
	subject: auditSubjectSchema.optional(),
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

/** An identity field as the form shows it: the industry by its label, every other field as it is stored. */
function identityFieldText(field: IdentityField, identity: CompanyIdentity): string {
	return field === "industry" && identity.industry !== "" ? INDUSTRY_LABELS[identity.industry] : identity[field];
}

/** The fields that differ between two versions of a company identity, in form order. */
export function describeIdentityChanges(before: CompanyIdentity, after: CompanyIdentity): AuditChange[] {
	return IDENTITY_FIELDS.filter(field => before[field] !== after[field]).map(field => ({
		label: IDENTITY_FIELD_LABELS[field],
		value: identityFieldText(field, after),
		previous: identityFieldText(field, before),
	}));
}

/** What differs between two versions of a member: the name, the role and each area whose access changed. */
export function describeMemberChanges(before: TeamMember, after: TeamMember): AuditChange[] {
	const changes: AuditChange[] = [];
	if (before.name !== after.name) {
		changes.push({ label: "Name", value: after.name, previous: before.name });
	}
	if (before.role !== after.role) {
		changes.push({
			label: "Role",
			value: MEMBER_ROLE_LABELS[after.role],
			previous: MEMBER_ROLE_LABELS[before.role],
		});
	}
	for (const area of ACCESS_AREAS) {
		if (before.access[area] !== after.access[area]) {
			changes.push({
				label: ACCESS_AREA_LABELS[area],
				value: ACCESS_LEVEL_LABELS[after.access[area]],
				previous: ACCESS_LEVEL_LABELS[before.access[area]],
			});
		}
	}
	return changes;
}

/** A new member's values: everything they were added with, and nothing before it. */
export function describeNewMember(member: TeamMember): AuditChange[] {
	return [
		{ label: "Name", value: member.name },
		{ label: "Email", value: member.email },
		{ label: "Role", value: MEMBER_ROLE_LABELS[member.role] },
		...ACCESS_AREAS.map(area => ({
			label: ACCESS_AREA_LABELS[area],
			value: ACCESS_LEVEL_LABELS[member.access[area]],
		})),
	];
}

/** The audit log comes a page of this many entries at a time, newest first. */
export const AUDIT_LOG_PAGE_SIZE = 10;

/** A link to another page of the audit log. The API makes it; a client passes it back unchanged and never reads it. */
export const auditLogPageTokenSchema = z.string().regex(/^[\w-]{1,512}$/, "Invalid page");

/**
 * The query of `GET /api/audit-log`: a section to show, or a subject whose entries to show ("document:<file id>"), and
 * the page to show, the newest when there is none. A section and a subject are each read from an index of their own, so
 * a query names one at most.
 */
export const auditLogQuerySchema = z
	.object({
		section: z.enum(AUDIT_SECTIONS).optional(),
		subject: z
			.string()
			.max(160)
			.refine(parameter => parseAuditSubjectParameter(parameter) !== undefined, "Invalid subject")
			.optional(),
		page: auditLogPageTokenSchema.optional(),
	})
	.refine(({ section, subject }) => section === undefined || subject === undefined, {
		message: "Name a section or a subject, not both",
		path: ["subject"],
	});
export type AuditLogQuery = z.infer<typeof auditLogQuerySchema>;

/** One page of the audit log, newest first, with links to the pages beside it: `null` where there is none. */
export const auditLogPageSchema = z.object({
	entries: z.array(auditEntrySchema).max(AUDIT_LOG_PAGE_SIZE),
	/** Older entries. */
	next: auditLogPageTokenSchema.nullable(),
	/** Newer entries. */
	previous: auditLogPageTokenSchema.nullable(),
});
export type AuditLogPage = z.infer<typeof auditLogPageSchema>;

/** Who is signed in, and to which organization. */
export const sessionSchema = z.object({
	organizationId: z.string().min(1),
	member: teamMemberSchema,
});
export type Session = z.infer<typeof sessionSchema>;

export const signInRequestSchema = z.object({
	email: emailSchema,
	password: z.string().min(1, "Password is required").max(256, "Use at most 256 characters"),
});
export type SignInRequest = z.infer<typeof signInRequestSchema>;

/** The API has set the session cookies. */
export const signedInResponseSchema = z.object({
	status: z.literal("signed-in"),
	session: sessionSchema,
});
export type SignedInResponse = z.infer<typeof signedInResponseSchema>;

/**
 * A member added under Team & Access signed in with the temporary password from their invitation and must choose their
 * own before the API opens a session. `challengeSession` is valid for 3 minutes.
 */
export const newPasswordRequiredResponseSchema = z.object({
	status: z.literal("new-password-required"),
	email: z.email(),
	challengeSession: z.string().min(1),
});
export type NewPasswordRequiredResponse = z.infer<typeof newPasswordRequiredResponseSchema>;

/**
 * Someone created an organization, or signed in before confirming the one they created, and must type the 6-digit code
 * Cognito emailed them. Nothing of the organization exists until they do, and no session cookies are set.
 */
export const emailConfirmationRequiredResponseSchema = z.object({
	status: z.literal("email-confirmation-required"),
	email: z.email(),
});
export type EmailConfirmationRequiredResponse = z.infer<typeof emailConfirmationRequiredResponseSchema>;

/** A 6-digit code from an authenticator app. */
export const totpCodeSchema = z
	.string()
	.trim()
	.regex(/^\d{6}$/, "Enter the 6-digit code from your authenticator app");

/**
 * The member has an authenticator app and must type its code before the API opens a session. `challengeSession` is
 * valid for 3 minutes, as the new-password challenge is.
 */
export const mfaRequiredResponseSchema = z.object({
	status: z.literal("mfa-required"),
	email: z.email(),
	challengeSession: z.string().min(1),
});
export type MfaRequiredResponse = z.infer<typeof mfaRequiredResponseSchema>;

export const answerMfaChallengeRequestSchema = z.object({
	email: emailSchema,
	challengeSession: z.string().min(1),
	code: totpCodeSchema,
});
export type AnswerMfaChallengeRequest = z.infer<typeof answerMfaChallengeRequestSchema>;

/**
 * The member's organization requires an authenticator app and this member has none. No session is opened and no
 * cookies are set: they set one up with /api/session/mfa/setup and /api/session/mfa/verify, which needs the same
 * email and password they just signed in with.
 */
export const mfaSetupRequiredResponseSchema = z.object({
	status: z.literal("mfa-setup-required"),
	email: z.email(),
});
export type MfaSetupRequiredResponse = z.infer<typeof mfaSetupRequiredResponseSchema>;

export const signInResponseSchema = z.discriminatedUnion("status", [
	signedInResponseSchema,
	newPasswordRequiredResponseSchema,
	emailConfirmationRequiredResponseSchema,
	mfaRequiredResponseSchema,
	mfaSetupRequiredResponseSchema,
]);
export type SignInResponse = z.infer<typeof signInResponseSchema>;

/** What a request that would have opened a session answers: the session, or the setup the organization insists on. */
export const sessionOpenedResponseSchema = z.discriminatedUnion("status", [
	signedInResponseSchema,
	mfaSetupRequiredResponseSchema,
]);
export type SessionOpenedResponse = z.infer<typeof sessionOpenedResponseSchema>;

export const startMfaSetupRequestSchema = z.object({
	email: emailSchema,
	password: signInRequestSchema.shape.password,
});
export type StartMfaSetupRequest = z.infer<typeof startMfaSetupRequestSchema>;

export const verifyMfaSetupRequestSchema = startMfaSetupRequestSchema.extend({ code: totpCodeSchema });
export type VerifyMfaSetupRequest = z.infer<typeof verifyMfaSetupRequestSchema>;

export const completeNewPasswordRequestSchema = z.object({
	email: emailSchema,
	challengeSession: z.string().min(1),
	newPassword: newPasswordSchema,
});
export type CompleteNewPasswordRequest = z.infer<typeof completeNewPasswordRequestSchema>;

export const changePasswordRequestSchema = z.object({
	currentPassword: z.string().min(1, "Current password is required").max(256, "Use at most 256 characters"),
	newPassword: newPasswordSchema,
});
export type ChangePasswordRequest = z.infer<typeof changePasswordRequestSchema>;

export const createOrganizationRequestSchema = z.object({
	companyName: companyIdentitySchema.shape.companyName,
	/** The name of the person creating the organization, who becomes its owner. */
	name: z.string().trim().min(1, "Your name is required").max(80, "Use at most 80 characters"),
	email: emailSchema,
	password: newPasswordSchema,
});
export type CreateOrganizationRequest = z.infer<typeof createOrganizationRequestSchema>;

/** Confirms a new organization with the code from the email, and the email and password it was created with. */
export const confirmOrganizationRequestSchema = z.object({
	email: emailSchema,
	password: signInRequestSchema.shape.password,
	code: z
		.string()
		.trim()
		.regex(/^\d{6}$/, "Enter the 6-digit code from the email"),
});
export type ConfirmOrganizationRequest = z.infer<typeof confirmOrganizationRequestSchema>;

/** What the forgot-password form sends: the address a reset code should go to. */
export const requestPasswordResetRequestSchema = z.object({ email: emailSchema });
export type RequestPasswordResetRequest = z.infer<typeof requestPasswordResetRequestSchema>;

/**
 * The API has emailed a reset code, if the address has an account that can take one. The answer is the same either
 * way, so nobody learns from it who uses Ravnar.
 */
export const passwordResetCodeSentResponseSchema = z.object({
	status: z.literal("password-reset-code-sent"),
	email: z.email(),
});
export type PasswordResetCodeSentResponse = z.infer<typeof passwordResetCodeSentResponseSchema>;

/** What the reset form sends: the code from the email and the password to set. */
export const confirmPasswordResetRequestSchema = z.object({
	email: emailSchema,
	code: confirmOrganizationRequestSchema.shape.code,
	newPassword: newPasswordSchema,
});
export type ConfirmPasswordResetRequest = z.infer<typeof confirmPasswordResetRequestSchema>;

/** The shared secret an authenticator app is set up with, as base32. */
export const mfaSecretResponseSchema = z.object({ secret: z.string().min(1) });
export type MfaSecretResponse = z.infer<typeof mfaSecretResponseSchema>;

/** One code from an authenticator app: what confirms a setup. */
export const verifyMfaCodeRequestSchema = z.object({ code: totpCodeSchema });
export type VerifyMfaCodeRequest = z.infer<typeof verifyMfaCodeRequestSchema>;

/** Whether the signed-in member signs in with an authenticator app, and whether their organization insists on one. */
export const profileMfaSchema = z.object({ enabled: z.boolean(), requiredByOrganization: z.boolean() });
export type ProfileMfa = z.infer<typeof profileMfaSchema>;

/** The product name every authenticator app files a Ravnar account under. */
export const MFA_ISSUER = "Ravnar";

/**
 * What an authenticator app scans or a phone opens: the `otpauth://` URI of RFC 6238's de facto Key Uri Format. The
 * label carries the issuer as well as the email, which is what makes apps group Ravnar's accounts together.
 */
export function otpauthUri(email: string, secret: string): string {
	// No URLSearchParams: this package's lib is deliberately DOM- and Node-free, so it is built by hand.
	const label = encodeURIComponent(`${MFA_ISSUER}:${email}`);
	const query = `secret=${encodeURIComponent(secret)}&issuer=${encodeURIComponent(MFA_ISSUER)}`;
	return `otpauth://totp/${label}?${query}`;
}
